<template>
  <div class="space-y-6">
    <!-- Champ de recherche d'adresse -->
    <div class="relative">
      <label class="block text-sm font-medium text-black">
        Rechercher une adresse <span class="text-red-500">*</span>
      </label>
      
      <div class="relative">
        <input
          type="text"
          :value="searchQuery"
          @input="handleSearchInput"
          @focus="showSuggestions = true"
          @blur="handleBlur"
          placeholder="Entrez votre adresse (ex: 123 rue de la Paix)"
          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-base"
        />
        
        <!-- Icône de recherche -->
        <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <i class="fas fa-search text-black"></i>
        </div>

        <!-- Indicateur de chargement -->
        <div v-if="isLoading" class="absolute inset-y-0 right-0 pr-12 flex items-center pointer-events-none">
          <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
        </div>
        
        <!-- Suggestions d'adresse -->
        <div 
          v-if="showSuggestions && suggestions.length > 0"
          class="absolute z-50 w-full mt-3 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-80 overflow-y-auto"
          style="top: 100%; left: 0;"
        >
          <div 
            v-for="(suggestion, index) in suggestions"
            :key="index"
            @click="selectAddress(suggestion)"
            class="px-5 py-4 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors hover:shadow-sm"
          >
            <div class="flex items-center space-x-4">
              <div class="flex-shrink-0">
                <i class="fas fa-map-marker-alt text-blue-500 text-lg"></i>
              </div>
              <div class="flex-1 min-w-0">
                <div class="font-semibold text-black text-base leading-tight">{{ suggestion.streetAddress }}</div>
                <div class="text-sm text-black mt-1">{{ suggestion.postalCode }} {{ suggestion.city }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Aucun résultat -->
        <div v-if="showSuggestions && !isLoading && suggestions.length === 0" class="absolute z-40 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-md p-4 text-sm text-black" style="top: 100%; left: 0;">
          Aucune adresse trouvée. Vous pouvez saisir l'adresse manuellement ci-dessous.
        </div>
      </div>

      <!-- Basculer en mode manuel -->
      <div class="mt-2">
        <button 
          type="button" 
          class="text-sm text-blue-600 hover:text-blue-700 underline" 
          @click="toggleManualMode">
          {{ manualMode ? 'Revenir à la recherche d\'adresse' : 'Saisir l\'adresse manuellement' }}
        </button>
      </div>
    </div>

    <!-- Champs détaillés de l'adresse -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <!-- Champ Numéro et rue (fusionné) -->
       <div>
        <label class="block text-sm font-medium text-black">
          Numéro <span class="text-red-500">*</span>
        </label>
        <input
          type="number"
          :value="streetNumber"
          @input="onStreetInput"
          placeholder="Numéro de rue"
          :disabled="!manualMode"
          :class="[
            'w-full px-3 py-2 border rounded-lg',
            manualMode ? 'border-gray-300 bg-white text-black focus:ring-2 focus:ring-blue-500 focus:border-blue-500' : 'border-gray-300 bg-gray-100 cursor-not-allowed text-black'
          ]"
        />
      </div>

      <div>
        <label class="block text-sm font-medium text-black">
          Rue <span class="text-red-500">*</span>
        </label>
        <input
          type="text"
          :value="streetAddress"
          @input="onStreetInput"
          placeholder="Rue"
          :disabled="!manualMode"
          :class="[
            'w-full px-3 py-2 border rounded-lg',
            manualMode ? 'border-gray-300 bg-white text-black focus:ring-2 focus:ring-blue-500 focus:border-blue-500' : 'border-gray-300 bg-gray-100 cursor-not-allowed text-black'
          ]"
        />
      </div>
    </div>

    <!-- Champ Ville -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <!-- Champ Code postal -->
      <div>
        <label class="block text-sm font-medium text-black">
          Code postal <span class="text-red-500">*</span>
        </label>
        <input
          type="text"
          :value="postalCode"
          @input="onPostalInput"
          placeholder="Code postal"
          inputmode="numeric"
          pattern="[0-9]*"
          maxlength="5"
          :disabled="!manualMode"
          :class="[
            'w-full px-3 py-2 border rounded-lg',
            manualMode ? 'border-gray-300 bg-white text-black focus:ring-2 focus:ring-blue-500 focus:border-blue-500' : 'border-gray-300 bg-gray-100 cursor-not-allowed text-black'
          ]"
        />
        <p v-if="postalCode && !isPostalValid" class="mt-1 text-xs text-red-600">Code postal invalide (5 chiffres)</p>
      </div>

      <div>
        <label class="block text-sm font-medium text-black">
          Ville <span class="text-red-500">*</span>
        </label>
        <input
          type="text"
          :value="city"
          @input="onCityInput"
          placeholder="Ville"
          :disabled="!manualMode"
          :class="[
            'w-full px-3 py-2 border rounded-lg',
            manualMode ? 'border-gray-300 bg-white text-black focus:ring-2 focus:ring-blue-500 focus:border-blue-500' : 'border-gray-300 bg-gray-100 cursor-not-allowed text-black'
          ]"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import type { AddressSuggestion, AddressAutocompleteFieldProps } from '@/types/sellerRegister'

const props   = defineProps<AddressAutocompleteFieldProps>()

const emit    = defineEmits<{
  'update:model-value': [value: string]
  'address-selected'  : [address: AddressSuggestion]
  'update:postal-code': [value: string]
  'update:city'       : [value: string]
  'postal-validation-changed': [isValid: boolean]
}>()

// Variables locales pour chaque champ
const searchQuery   = ref('');
const streetNumber  = ref();
const streetAddress = ref('');
const postalCode    = ref('');
const city          = ref('');
const manualMode    = ref(false)

// Validation du code postal (France: 5 chiffres)
const isPostalValid = computed(() => /^\d{5}$/.test(postalCode.value))

// Synchronisation avec les props du composant parent
watch(()    => props.modelValue, (newValue) => {
  streetAddress.value = newValue || ''
}, { immediate: true })

// Synchronisation des champs d'adresse depuis le composant parent
watch(() => props.postalCode, (newValue) => {
  if (newValue) postalCode.value = newValue
}, { immediate: true })

watch(() => props.city, (newValue) => {
  if (newValue) city.value = newValue
}, { immediate: true })

const showSuggestions = ref(false)
const suggestions     = ref<AddressSuggestion[]>([])
const isLoading       = ref(false)

// Debounce pour éviter trop de requêtes
let searchTimeout: number

// Cache local pour éviter les requêtes répétées
const addressCache    = new Map<string, AddressSuggestion[]>()

// Handler pour le champ de recherche
const handleSearchInput = (event: Event) => {
  const value       = (event.target as HTMLInputElement).value
  searchQuery.value = value

  // Annuler la recherche précédente
  clearTimeout(searchTimeout)
  
  // Nouvelle recherche après 300ms d'inactivité
  searchTimeout     = setTimeout(() => {
    if (value.length >= 3) {
      searchAddresses(value)
    } else {
      suggestions.value = []
    }
  }, 300)
}

const API_BASE = (process.env.VUE_APP_API_BASE_URL || '').replace(/\/$/, '')

const buildApiUrl = (path: string) => {
  // If a base URL is provided (production), prefix it; otherwise use relative path for dev proxy
  return API_BASE ? `${API_BASE}${path}` : path
}

const searchAddresses = async (query: string) => {
  // Vérifier le cache d'abord
  if (addressCache.has(query)) {
    suggestions.value = addressCache.get(query)!
    return
  }

  isLoading.value = true
  
  try {

    const url = buildApiUrl(`/api/places/autocomplete?input=${encodeURIComponent(query)}&types=address&components=country:fr&language=fr`)
    const response = await fetch(url)
    
    if (!response.ok) {
      throw new Error(`Erreur Google Places API: ${response.status}`)
    }
    
    const data = await response.json()
    
    if (data.status !== 'OK') {
      throw new Error(`Erreur API: ${data.status}`)
    }
    
    if (!data.predictions || data.predictions.length === 0) {
      suggestions.value = []
      // Activer la saisie manuelle si rien n'est trouvé
      manualMode.value = true
      return
    }
    
    // Transformer les prédictions Google Places
    const transformedSuggestions = await Promise.all(
      data.predictions.map(async (prediction: any) => {
        // Récupérer les détails de l'adresse via backend proxy
        const detailsUrl      = buildApiUrl(`/api/places/details?place_id=${prediction.place_id}&fields=address_components,formatted_address&language=fr`)
        const detailsResponse = await fetch(detailsUrl)
        const detailsData     = await detailsResponse.json()
        
        if (detailsData.status !== 'OK') {
          return null
        }
        
        // Extraire les composants d'adresse
        const addressComponents = detailsData.result.address_components
        let streetNumber        = ''
        let streetName          = ''
        let postalCode          = ''
        let city                = ''

        addressComponents.forEach((component: any) => {
          const types = component.types
          
          if (types.includes('street_number')) {
            streetNumber = component.long_name
          } else if (types.includes('route')) {
            streetName = component.long_name
          } else if (types.includes('locality')) {
            city = component.long_name
          } else if (types.includes('postal_code')) {
            postalCode = component.long_name
          }
        })
        
        const streetAddress = streetNumber && streetName ? `${streetNumber} ${streetName}` : streetName || prediction.description.split(',')[0]
        
        return {
          streetNumber,
          streetName,
          streetAddress,
          postalCode,
          city,
          fullAddress   : prediction.description
        }
      })
    )
    
    // Filtrer les suggestions valides
    const validSuggestions = transformedSuggestions.filter(suggestion => 
      suggestion && suggestion.streetAddress && suggestion.city
    )
    
    suggestions.value = validSuggestions
    
    // Mettre en cache
    if (addressCache.size < 100) {
      addressCache.set(query, validSuggestions)
    }
    
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Address autocomplete error:', error)
    suggestions.value = []
    // En cas d'erreur, permettre la saisie manuelle
    manualMode.value = true
  } finally {
    isLoading.value = false
  }
}

const selectAddress = (address: AddressSuggestion & { streetName: string, streetNumber: string }) => {
  // Remplir tous les champs
  // console.log(address)
  streetNumber.value  = parseInt(address.streetNumber)
  streetAddress.value = address.streetName
  postalCode.value    = address.postalCode
  city.value          = address.city
  
  // Vider le champ de recherche
  searchQuery.value   = ''
  
  // Émettre les événements individuels pour chaque champ
  emit('update:model-value', address.streetAddress)
  emit('update:postal-code', address.postalCode)
  emit('update:city', address.city)
  emit('postal-validation-changed', /^\d{5}$/.test(address.postalCode))
  emit('address-selected', address)
  
  showSuggestions.value = false
  suggestions.value = []
}

const handleBlur = () => {
  // Délai pour permettre le clic sur les suggestions
  setTimeout(() => {
    showSuggestions.value = false
  }, 200)
}

// Gestion des bascules de mode
const toggleManualMode = () => {
  manualMode.value = !manualMode.value
  if (!manualMode.value) {
    // Retour à la recherche: vider les champs (la sélection remplira à nouveau)
    streetAddress.value = props.modelValue || ''
    postalCode.value = props.postalCode || ''
    city.value = props.city || ''
  }
}

// Gestion des saisies manuelles avec émission vers le parent
const onStreetInput = (e: Event) => {
  const val = (e.target as HTMLInputElement).value
  streetAddress.value = val
  emit('update:model-value', val)
}

const onPostalInput = (e: Event) => {
  const raw = (e.target as HTMLInputElement).value
  // Garder uniquement les chiffres et limiter à 5
  const cleaned = raw.replace(/\D/g, '').slice(0, 5)
  postalCode.value = cleaned
  emit('update:postal-code', cleaned)
  emit('postal-validation-changed', /^\d{5}$/.test(cleaned))
}

const onCityInput = (e: Event) => {
  const val = (e.target as HTMLInputElement).value
  city.value = val
  emit('update:city', val)
}
</script>

<style scoped>
/* Styles personnalisés si nécessaire */
</style>